import { z } from 'zod';

import type {
  GoogleStartRequest,
  MagicLinkRequest,
  MagicLinkVerifyRequest,
  StaffLoginRequest,
} from '@nucleus-commerce/api-client/admin';

import type { LoginValues } from '../schemas/login-schema';
import type { CodeMethod } from '../schemas/two-factor-rules';
import type { MagicLinkRequestValues } from '../schemas/two-factor-schemas';

/** Authenticator codes go out as six bare digits. */
export function toTotpCode(code: string): string {
  return code.replace(/\s+/g, '');
}

/** Recovery codes go out lower-cased with the hyphen, the way they were issued. */
export function toRecoveryCode(code: string): string {
  const compact = code.trim().toLowerCase();
  return compact.includes('-') ? compact : `${compact.slice(0, 4)}-${compact.slice(4)}`;
}

export function toChallengeCode(code: string, method: CodeMethod): string {
  return method === 'totp' ? toTotpCode(code) : toRecoveryCode(code);
}

/** The backend puts the slug into the emailed link as `&organization=`; empty means the default. */
export function toMagicLinkRequest(values: MagicLinkRequestValues): MagicLinkRequest {
  const slug = values.organizationSlug.trim();
  return slug ? { email: values.email, organizationSlug: slug } : { email: values.email };
}

export function toMagicLinkVerify(
  token: string,
  organizationSlug: string | null | undefined,
): MagicLinkVerifyRequest {
  const slug = organizationSlug?.trim();
  return slug ? { token, organizationSlug: slug } : { token };
}

/** The downloadable recovery-codes file: translated header lines, a blank line, one code per line. */
export function recoveryCodesText(header: readonly string[], codes: readonly string[]): string {
  return [...header, '', ...codes, ''].join('\n');
}

/** An empty organisation means the default one, so the key is left out. */
export function toStaffLogin(values: LoginValues): StaffLoginRequest {
  const slug = values.organizationSlug?.trim();
  return slug
    ? { email: values.email, password: values.password, organizationSlug: slug }
    : { email: values.email, password: values.password };
}

/** The typed email pre-selects the Google account when it looks like one. */
export function toGoogleStart(
  email: string,
  organizationSlug: string | null | undefined,
): GoogleStartRequest {
  const slug = organizationSlug?.trim();
  const hint = email.trim();
  return {
    ...(slug ? { organizationSlug: slug } : {}),
    ...(z.email().safeParse(hint).success ? { loginHint: hint } : {}),
  };
}
